import React, { useState, useEffect } from "react";
import { Button } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import {
  FooterWrapper,
  FooterPlaceholder,
  FooterContainer,
  FooterDisclaimer,
  FooterContent,
  TotalFee,
  FooterButtons,
} from "./TreatmentPlanFooter.styled";
import { Popover, SharePopoverContent } from "../../../../components/Popover";
import { DownloadIcon } from "../../../../components/Icons";
import { colors } from "../../../../theme/colors";

interface TreatmentPlanFooterProps {
  totalFee: string | number | null | undefined;
  status: number;
  isProvider: boolean;
  hasMultipleOptions?: boolean;
  isSummarizedView?: boolean;
  canBeReapproved?: boolean;
  onApprove: () => void;
  onReApprove?: () => void;
  onDownloadPdf: () => void;
  onDownloadSummarizedPdf?: () => void;
  onEmailForReview: () => void;
  onOpenToSign: () => void;
  actionsDisabled?: boolean;
  actionsDisabledReason?: string;
}

/**
 * Treatment plan footer with total fee and action buttons
 *
 * Status logic (according to requirements):
 * Scenario 1 - status 1, 2 (AI Drafted, Awaiting Provider Review):
 *   - "Approve" button active
 *   - "Share with Patient" button disabled
 *
 * Scenario 2 - status 4, 5, 6, 7 (Declined, Approved, InProgress, Completed):
 *   - "Download as PDF" button only
 *
 * Scenario 3 - status 3 (Awaiting Patient Confirmation):
 *   - "Share with Patient" button active with popover (PDF, Email, Sign)
 */
const TreatmentPlanFooter: React.FC<TreatmentPlanFooterProps> = ({
  totalFee,
  status,
  isProvider,
  hasMultipleOptions = false,
  isSummarizedView = false,
  canBeReapproved = false,
  onApprove,
  onReApprove,
  onDownloadPdf,
  onDownloadSummarizedPdf,
  onEmailForReview,
  onOpenToSign,
  actionsDisabled = false,
  actionsDisabledReason = "",
}) => {
  const { t } = useTranslation();
  const [shareAnchorEl, setShareAnchorEl] = useState<HTMLElement | null>(null);
  const isSharePopoverOpen = Boolean(shareAnchorEl);

  const isAwaitingProviderReview = status === 1 || status === 2;
  const isAfterApproval =
    status === 4 || status === 5 || status === 6 || status === 7;
  const isAwaitingPatientConfirmation = status === 3;

  useEffect(() => {
    if (actionsDisabled) {
      setShareAnchorEl(null);
    }
  }, [actionsDisabled]);

  const handleShareClick = (event: React.MouseEvent<HTMLElement>) => {
    setShareAnchorEl(event.currentTarget);
  };

  const handleShareClose = () => {
    setShareAnchorEl(null);
  };

  const handleDownloadPdf = () => {
    handleShareClose();
    onDownloadPdf();
  };

  const handleEmailForReview = () => {
    handleShareClose();
    onEmailForReview();
  };

  const handleOpenToSign = () => {
    handleShareClose();
    onOpenToSign();
  };

  const formatFee = () => {
    if (totalFee === null || totalFee === undefined) return "$0";
    if (typeof totalFee === "number")
      return `$${totalFee.toLocaleString("en-US")}`;
    if (typeof totalFee === "string") {
      return totalFee.startsWith("$") ? totalFee : `$${totalFee}`;
    }
    return "$0";
  };
  const formattedFee = formatFee();
  const wrapDisabledWithTooltip = (button: React.ReactNode) => {
    if (!actionsDisabled || !actionsDisabledReason) return button;
    return (
      <Tooltip title={actionsDisabledReason} arrow placement="top">
        <span>{button}</span>
      </Tooltip>
    );
  };

  const renderButtons = () => {
    if (!isProvider) return null;

    if (isSummarizedView) {
      return (
        <FooterButtons>
          {wrapDisabledWithTooltip(
            <Button
              variant="contained"
              onClick={onDownloadSummarizedPdf}
              disabled={actionsDisabled}
              startIcon={
                <DownloadIcon
                  width="16px"
                  height="16px"
                  fill={colors.text.white}
                />
              }
              sx={{
                backgroundColor: colors.primary.main,
                "&:hover": { backgroundColor: colors.primary.dark },
              }}
            >
              {t("treatmentPlan.downloadAsPdf")}
            </Button>,
          )}
        </FooterButtons>
      );
    }

    if (isAwaitingProviderReview) {
      return (
        <FooterButtons>
          <Button
            variant="contained"
            disabled
            sx={{
              backgroundColor: colors.amber.main,
              "&:hover": {
                backgroundColor: colors.amber.dark,
              },
            }}
          >
            {t("treatmentPlan.shareWithPatient")}
          </Button>
          {wrapDisabledWithTooltip(
            <Button
              variant="contained"
              onClick={onApprove}
              disabled={actionsDisabled}
              sx={{
                minWidth: "112px!important",
                background: colors.status.success,
              }}
            >
              {t("treatmentPlan.approve")}
            </Button>,
          )}
        </FooterButtons>
      );
    }

    if (isAfterApproval) {
      return (
        <FooterButtons>
          {wrapDisabledWithTooltip(
            <Button
              variant="contained"
              onClick={onDownloadPdf}
              disabled={actionsDisabled}
              sx={{
                backgroundColor: colors.primary.main,
                "&:hover": {
                  backgroundColor: colors.primary.dark,
                },
              }}
            >
              {t("treatmentPlan.downloadAsPdf")}
            </Button>,
          )}
        </FooterButtons>
      );
    }

    if (isAwaitingPatientConfirmation) {
      if (canBeReapproved) {
        return (
          <FooterButtons>
            {wrapDisabledWithTooltip(
              <Button
                variant="contained"
                onClick={onReApprove}
                disabled={actionsDisabled}
                sx={{
                  minWidth: "112px!important",
                  background: colors.status.success,
                }}
              >
                {t("reApproveConfirmation.reApproveButton")}
              </Button>,
            )}
          </FooterButtons>
        );
      }

      return (
        <FooterButtons>
          {wrapDisabledWithTooltip(
            <Button
              variant="contained"
              onClick={handleShareClick}
              disabled={actionsDisabled}
              sx={{
                backgroundColor: colors.amber.main,
                "&:hover": {
                  backgroundColor: colors.amber.dark,
                },
              }}
            >
              {t("treatmentPlan.shareWithPatient")}
            </Button>,
          )}
          <Popover
            open={isSharePopoverOpen}
            anchorEl={shareAnchorEl}
            onClose={handleShareClose}
          >
            <SharePopoverContent
              onDownloadPdf={handleDownloadPdf}
              onEmailForReview={handleEmailForReview}
              onOpenToSign={handleOpenToSign}
              status={status}
            />
          </Popover>
        </FooterButtons>
      );
    }

    return null;
  };

  const displayFee = hasMultipleOptions ? "$0,00" : formattedFee;

  return (
    <FooterWrapper>
      {hasMultipleOptions && (
        <FooterPlaceholder>
          {t("treatmentPlan.totalFeePlaceholder")}
        </FooterPlaceholder>
      )}
      <FooterContainer>
        <FooterDisclaimer>
          {t("treatmentPlan.timeEstimateDisclaimer")}
          <br />
          {t("treatmentPlan.feesDisclaimer")}
        </FooterDisclaimer>
        <FooterContent>
          <TotalFee>
            {isSummarizedView
              ? t("treatmentPlan.totalCareCost")
              : t("treatmentPlan.totalFee")}{" "}
            {displayFee}
          </TotalFee>
          {renderButtons()}
        </FooterContent>
      </FooterContainer>
    </FooterWrapper>
  );
};

export default TreatmentPlanFooter;
